const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8082/api';
const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL || `${API_BASE_URL}/v1/auth`;

// Token storage keys
const TOKEN_KEY = 'waide_access_token';
const REFRESH_TOKEN_KEY = 'waide_refresh_token';
const USER_KEY = 'waide_user';

class AuthService {
  /**
   * Login with username/email and password. `tenantId` is only needed on a
   * second call, after a first call without it comes back with
   * requiresTenantSelection=true (the same email matches more than one
   * tenant) -- see AuthenticationService.login on the backend.
   */
  static async login(username, password, tenantId) {
    try {
      const response = await fetch(`${AUTH_API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: username, password, tenantId: tenantId || undefined }),
      });

      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');

      if (response.ok && isJson) {
        const data = await response.json();

        if (data.requiresTenantSelection) {
          return {
            success: false,
            requiresTenantSelection: true,
            tenantOptions: data.tenantOptions || [],
          };
        }

        // Added 2026-09-29 (bulk user onboarding): valid credentials, but this
        // user is still on the ONE shared platform-wide default password
        // (User.mustChangePassword) -- no session tokens are issued yet. The
        // resetToken is a real password-reset token (same kind a forgot-password
        // email would carry), just issued in-band since the credentials just
        // checked are already sufficient proof -- LoginPage routes straight into
        // the existing /reset-password flow with it.
        if (data.requiresPasswordChange) {
          return {
            success: false,
            requiresPasswordChange: true,
            resetToken: data.resetToken,
          };
        }

        this.setTokens(data.accessToken, data.refreshToken);
        this.setUser(data.user);
        return { success: true, user: data.user };
      }

      if (isJson) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Authentication failed');
      }

      throw new Error('Authentication failed');
    } catch (error) {
      if (error.name === 'TypeError' || error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to server. Please try again later.');
      }
      throw error;
    }
  }

  /**
   * Register a new user
   */
  static async register(email, password, firstName, lastName, extra = {}) {
    try {
      const { companyName, tenantCode, warehouseCode } = extra;
      const response = await fetch(`${AUTH_API_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ email, password, firstName, lastName, companyName, tenantCode, warehouseCode }),
      });

      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');

      // Try to parse response body
      let data = null;
      if (isJson) {
        try {
          data = await response.json();
        } catch (e) {
          console.error('Failed to parse response JSON:', e);
        }
      }

      if (response.ok) {
        return { success: true, data };
      }

      // Handle error response
      console.error('Registration failed:', response.status, data);

      // Try to extract error message from various possible formats
      const errorMessage = data?.message
        || data?.error
        || data?.errors?.[0]?.message
        || data?.errors?.[0]
        || (typeof data === 'string' ? data : null)
        || `Registration failed (${response.status})`;

      throw new Error(errorMessage);
    } catch (error) {
      if (error.name === 'TypeError' || error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to server. Please try again later.');
      }
      throw error;
    }
  }

  /**
   * Logout - clear all tokens and user data
   */
  static logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  /**
   * Refresh access token using refresh token
   */
  static async refreshToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${AUTH_API_URL}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        this.setTokens(data.accessToken, data.refreshToken || refreshToken);
        return data.accessToken;
      }

      // If refresh fails, logout
      this.logout();
      throw new Error('Session expired. Please login again.');
    } catch (error) {
      if (error.name === 'TypeError' || error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
        this.logout();
        throw new Error('Unable to connect to server. Please login again.');
      }
      throw error;
    }
  }

  /**
   * Store tokens in localStorage
   */
  static setTokens(accessToken, refreshToken) {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  /**
   * Store user data in localStorage
   */
  static setUser(user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  /**
   * Get current access token
   */
  static getAccessToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Get refresh token
   */
  static getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Get current user
   */
  static getUser() {
    const userStr = localStorage.getItem(USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated() {
    const token = this.getAccessToken();
    if (!token) return false;

    // Check if token is expired
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      return Date.now() < expiry;
    } catch {
      return false;
    }
  }

  /**
   * Check if token needs refresh (expires in less than 5 minutes)
   */
  static tokenNeedsRefresh() {
    const token = this.getAccessToken();
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000;
      const fiveMinutes = 5 * 60 * 1000;
      return Date.now() > (expiry - fiveMinutes);
    } catch {
      return false;
    }
  }

  /**
   * Get Authorization header value
   */
  static getAuthHeader() {
    const token = this.getAccessToken();
    return token ? `Bearer ${token}` : null;
  }

  /**
   * Server-side check for whether the current user's tenant plan tier may
   * open FAST Studio (StudioAccessController) -- called by FastStudioShell on
   * mount so a STARTER tenant can't reach /studio just by knowing/bookmarking
   * the URL, even though the header icon that links there is already hidden
   * for them. Fails open (true) on a network/parse error so a transient
   * backend hiccup doesn't lock out a legitimately-allowed tenant; the real
   * gate is server-side regardless of what this returns on a failure.
   */
  static async checkStudioAccess() {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/studio/access`, {
        headers: { Authorization: this.getAuthHeader() },
      });
      if (!response.ok) return false;
      const data = await response.json();
      return data.allowed === true;
    } catch {
      return false;
    }
  }

  /**
   * Live-computed default tenant/warehouse codes for the signup form, shown
   * as an editable starting point (not silently generated) -- see
   * UserService.suggestCodes. No auth header needed (pre-login).
   */
  static async suggestCodes(companyName) {
    const params = new URLSearchParams();
    if (companyName) params.set('companyName', companyName);
    const response = await fetch(`${AUTH_API_URL}/suggest-codes?${params.toString()}`);
    if (!response.ok) return { tenantCode: '', warehouseCode: '' };
    return response.json();
  }

  /**
   * Live as-you-type availability check while editing the signup form.
   * Real bug found live 2026-10-01: this used to collapse ANY failure
   * (network error, a transient 500, or -- the actual repro -- the shared
   * RateLimitingFilter kicking in after suggestCodes/checkTenantCode/
   * checkWarehouseCode all fire on every keystroke pause) into
   * `{ available: false }`, which the UI renders as "already taken" --
   * permanently, for every code, with no way to tell a real conflict from
   * a failed check. Callers must check `error` and treat it as "unknown",
   * never as "taken".
   */
  static async checkTenantCode(code) {
    if (!code) return { available: false };
    try {
      const response = await fetch(`${AUTH_API_URL}/check-tenant-code?code=${encodeURIComponent(code)}`);
      if (!response.ok) return { available: null, error: true };
      return response.json();
    } catch (err) {
      return { available: null, error: true };
    }
  }

  /** Live as-you-type availability check while editing the signup form. Same failure handling as checkTenantCode. */
  static async checkWarehouseCode(code) {
    if (!code) return { available: false };
    try {
      const response = await fetch(`${AUTH_API_URL}/check-warehouse-code?code=${encodeURIComponent(code)}`);
      if (!response.ok) return { available: null, error: true };
      return response.json();
    } catch (err) {
      return { available: null, error: true };
    }
  }

  /**
   * Record acceptance of the given Terms & Conditions version for the current
   * user, then update the locally-cached user object so
   * TermsAcceptanceModal's gate (AtomApp.js) re-evaluates without a fresh
   * login. Merges only termsAcceptedAt/termsVersion into the EXISTING stored
   * user rather than replacing it wholesale -- the backend response here is
   * built via UserResponse.fromUser(user) (the plain overload), which is
   * thinner than the login response's connectedSystems/defaultConnectedSystem/
   * activeWarehouseId-populated shape; overwriting with it outright would
   * silently wipe that richer data the rest of the app (system selection,
   * warehouse context) depends on.
   */
  static async acceptTerms(termsVersion) {
    const response = await fetch(`${AUTH_API_URL}/accept-terms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: this.getAuthHeader(),
      },
      body: JSON.stringify({ termsVersion }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to record terms acceptance');
    }
    const thinResponse = await response.json();
    const existingUser = this.getUser() || {};
    const mergedUser = {
      ...existingUser,
      termsAcceptedAt: thinResponse.termsAcceptedAt,
      termsVersion: thinResponse.termsVersion,
    };
    this.setUser(mergedUser);
    return mergedUser;
  }

  /**
   * Fetch the full, authoritative current-user profile from the server
   * (GET /users/me -- includes connectedSystems/defaultConnectedSystem/
   * activeWarehouseId, unlike acceptTerms()'s thinner response) and replace
   * the cached copy with it wholesale.
   *
   * This exists specifically to self-heal a real, confirmed bug: the cached
   * `waide_user` object is trusted as-is on every app load with no server
   * round-trip (see AuthContext.js's checkAuth), so if it ever gets written
   * in a thin/partial shape by any code path, that corruption persists
   * forever with no way to recover short of a manual localStorage clear or
   * a fresh login. Confirmed live 2026-09-28: `acceptTerms()`'s merge
   * (`{...existingUser, termsAcceptedAt, termsVersion}`) is correct in
   * isolation, but if `existingUser` was ALREADY thin when it ran, the
   * merge just perpetuates that thinness -- the stored user was reduced to
   * exactly `{termsAcceptedAt, termsVersion}`, `connectedSystems` went
   * missing, and the app was stuck on a permanent blocking spinner (the
   * facility-setup check silently never fires without a resolved connected
   * system -- see AtomApp.js). AuthContext.js calls this once in the
   * background right after the instant cache-based paint on every mount,
   * so this whole class of stale/corrupted-cache bug self-heals within one
   * page load instead of persisting indefinitely.
   */
  static async fetchCurrentUser() {
    const response = await fetch(`${API_BASE_URL}/v1/users/me`, {
      headers: { Authorization: this.getAuthHeader() },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch current user: HTTP ${response.status}`);
    }
    const freshUser = await response.json();
    this.setUser(freshUser);
    return freshUser;
  }

  /**
   * Lists every warehouse the current user has access to, plus which one is
   * currently active -- backs the warehouse switcher. Pre-existing backend
   * endpoint (UserController.getUserWarehouses), never wired to any
   * frontend UI until now.
   */
  static async getMyWarehouses() {
    const response = await fetch(`${API_BASE_URL}/v1/users/me/warehouses`, {
      headers: { Authorization: this.getAuthHeader() },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch warehouses: HTTP ${response.status}`);
    }
    return response.json();
  }

  /**
   * Switches the user's default/active warehouse (persists as their new
   * default -- CustomUserDetailsService re-resolves it from
   * user_warehouse_access.is_default on every subsequent request, so this
   * takes effect immediately with no new token needed). Caller is
   * responsible for reloading the app afterward so already-loaded,
   * warehouse-scoped data (grids, dashboards) re-fetches under the new
   * scope -- see the warehouse switcher in StatusBar.js.
   */
  static async switchActiveWarehouse(warehouseId) {
    const response = await fetch(`${API_BASE_URL}/v1/users/me/active-warehouse`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: this.getAuthHeader(),
      },
      body: JSON.stringify({ warehouseId }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.error || `Failed to switch warehouse: HTTP ${response.status}`);
    }
    return data;
  }

  /**
   * Read-only check of whether a verification token is valid — does NOT consume it.
   * Safe to call automatically on page load (e.g. an email link-scanner prefetching
   * the link hits this same GET and can't burn the token).
   */
  static async checkVerifyEmailToken(token) {
    try {
      const response = await fetch(`${AUTH_API_URL}/verify-email?token=${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Unable to check verification token');
      }

      return await response.json(); // { valid: true/false }
    } catch (error) {
      if (error.name === 'TypeError' || error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to server. Please try again later.');
      }
      throw error;
    }
  }

  /**
   * Confirm email verification — consumes the token. Only call this from an
   * explicit user action (e.g. a button click), never automatically on page load.
   */
  static async confirmVerifyEmail(token) {
    try {
      const response = await fetch(`${AUTH_API_URL}/verify-email/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');

      let data = null;
      if (isJson) {
        try {
          data = await response.json();
        } catch (e) {
          console.error('Failed to parse response JSON:', e);
        }
      }

      if (response.ok) {
        return data;
      }

      const errorMessage = data?.message || 'Email verification failed';
      throw new Error(errorMessage);
    } catch (error) {
      if (error.name === 'TypeError' || error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to server. Please try again later.');
      }
      throw error;
    }
  }

  /**
   * Resend verification email
   */
  static async resendVerification(email) {
    try {
      const response = await fetch(`${AUTH_API_URL}/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');

      let data = null;
      if (isJson) {
        try {
          data = await response.json();
        } catch (e) {
          console.error('Failed to parse response JSON:', e);
        }
      }

      if (response.ok) {
        return data;
      }

      const errorMessage = data?.message || 'Failed to resend verification email';
      throw new Error(errorMessage);
    } catch (error) {
      if (error.name === 'TypeError' || error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to server. Please try again later.');
      }
      throw error;
    }
  }

  /**
   * Request a password reset email. Real bug fixed 2026-09-22: the "Forgot
   * Password" dialog in LoginPage.js used to be a pure stub -- a commented-out
   * call and a fake 1.5s delay before always showing success -- so it never
   * actually reached the backend at all.
   */
  static async requestPasswordReset(email) {
    try {
      const response = await fetch(`${AUTH_API_URL}/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      let data = null;
      if (isJson) {
        try {
          data = await response.json();
        } catch (e) {
          console.error('Failed to parse response JSON:', e);
        }
      }

      if (response.ok) {
        return data;
      }

      const errorMessage = data?.message || 'Failed to send reset email';
      throw new Error(errorMessage);
    } catch (error) {
      if (error.name === 'TypeError' || error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to server. Please try again later.');
      }
      throw error;
    }
  }

  /**
   * Read-only check of whether a password-reset token is valid -- does NOT
   * consume it. Safe to call automatically on page load, same reasoning as
   * checkVerifyEmailToken.
   */
  static async checkResetPasswordToken(token) {
    try {
      const response = await fetch(`${AUTH_API_URL}/validate-reset-token?token=${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Unable to check reset link');
      }

      return await response.json(); // { valid: true/false }
    } catch (error) {
      if (error.name === 'TypeError' || error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to server. Please try again later.');
      }
      throw error;
    }
  }

  /**
   * Confirm the password reset -- consumes the token, sets the new password.
   * Only call from an explicit user action (the reset form's submit), never
   * automatically on page load.
   */
  static async confirmResetPassword(token, newPassword) {
    try {
      const response = await fetch(`${AUTH_API_URL}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      });

      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      let data = null;
      if (isJson) {
        try {
          data = await response.json();
        } catch (e) {
          console.error('Failed to parse response JSON:', e);
        }
      }

      if (response.ok) {
        return data;
      }

      const errorMessage = data?.message
        || data?.errors?.[0]?.message
        || data?.errors?.[0]
        || 'Password reset failed';
      throw new Error(errorMessage);
    } catch (error) {
      if (error.name === 'TypeError' || error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to server. Please try again later.');
      }
      throw error;
    }
  }
}

export default AuthService;
